/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Ilya Baranchikov
 * @version 1.27.1
 * @since 1.13.0
 */

import { Fragment, useCallback, useEffect, useState } from 'react';

import { TooltipText, Typography } from '@nexign/scc-ui-kit';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import EllipsisText from '@/main/components/common/EllipsisText/EllipsisText';
import withAsyncStateManagement from '@/main/modules/asyncStateManagment/WithAsyncStateManagement';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { wizardStorageClearStepsAction } from '@/main/modules/wizard/wizardActions';
import { EWizardName } from '@/main/modules/wizard/wizardConstants';
import {
    makeSelectFeatureEnabled,
    makeSelectIsFeatureWithVersionEnabled,
} from '@/main/pages/PageAuth/selectors/authSelectors';
import { productsSagas } from '@/main/pages/PageProducts/productsSagas';
import { productsSetPageFromAction } from '@/main/pages/PageProducts/tariffsAndServicesActions';
import { EProductsCategoryUrl, EProductsPageFrom } from '@/main/pages/PageProducts/tariffsAndServicesConstants';
import { useSubscriberIdContext } from '@/main/pages/PageSubscriberId/context';
import { subscriberIdRegionIdSelector } from '@/main/pages/PageSubscriberId/selectors';
import { ESubscriberIdActions } from '@/main/pages/PageSubscriberId/types';
import { DangerouslySettedTextStyled } from '@/main/pages/WizardProductOfferings/components/styled/StepSelectMegaPowersStyled';
import { openRoute } from '@/main/routes/constants/externalRoutes';
import { CLIENT_ID, RoutePath } from '@/main/routes/constants/routesConstants';
import { addCommaToWordByIndex } from '@/main/utils/addCommasToWords';
import { formatDate } from '@/main/utils/formatText';
import { stringFormatParams } from '@/main/utils/stringFormat';
import { BadgeStatus, Button, LinkBlock, Skeleton } from '@components/common';
import { EBadgeStatus } from '@components/common/Badge/BadgeStatus';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';
import { ELocaleKeys } from '@modules/internationalization/intConstants';

import SubscriberIdError from '../../SubscriberIdError';
import { infoTranslationSelector } from '../selectors';
import { useGetTariffQuery } from '../store';
import { IInfoTariff } from '../types';
import { MegaPowersList, SubscriberIdInfoMegaPowersStyled } from './styled/SubscriberIdInfoStyled';
import { SubscriberIdInfoTariffSkeleton } from './SubscriberIdInfoSkeleton';
import SubscriberIdInfoTariffDiscounts from './SubscriberIdInfoTariffDiscounts';

const SubscriberIdInfoTariff = () => {
    const [isFullOptionsMode, setFullOptionsMode] = useState(false);
    const [optionsDrop, setOptionsDrop] = useState(0);

    const dispatch = useDispatch();

    const { subscriberId, userType } = useSubscriberIdContext();
    const regionId = useSelector(subscriberIdRegionIdSelector(subscriberId, userType));

    const translationTab = useSelector(infoTranslationSelector());

    const isMegaPowersFeature = useSelector(
        makeSelectIsFeatureWithVersionEnabled(FeatureNameEnum.MEGA_POWERS, 2, true),
    );
    const isChangesCalculatingRemainsAndRatePlanFeatureEnabled = useSelector(
        makeSelectFeatureEnabled(FeatureNameEnum.CHANGES_CALCULATING_REMAINS_AND_RATE_PLAN),
    );

    const {
        data: subscriberInfoTariff,
        isFetching: isSubscriberInfoTariffFetching,
        error: errorSubscriberInfoTariff,
        refetch,
    } = useGetTariffQuery(
        { subscriberId, regionId },
        {
            skip: !regionId,
        },
    );

    useEffect(() => {
        setOptionsDrop(2);
    }, [subscriberInfoTariff]);

    const formatProductPrice = useCallback(
        (ratePlan: IInfoTariff['ratePlan']) => {
            if (!ratePlan.payment) {
                return translationTab.tariffDiscounts.priceFree;
            }
            if (ratePlan.payment.error) {
                return translationTab.tariffDiscounts.priceError;
            }
            if (ratePlan.payment.unit && ratePlan.payment.value) {
                if (isChangesCalculatingRemainsAndRatePlanFeatureEnabled) {
                    return ratePlan.payment.periodLength
                        ? `${ratePlan.payment.value} ₽/ ${ratePlan.payment.periodLength} ${ratePlan.payment.unit}`
                        : `${ratePlan.payment.value} ₽/${ratePlan.payment.unit}`;
                } else {
                    return `${ratePlan.payment.value} ${ratePlan.payment.unit}`;
                }
            }
        },
        [isChangesCalculatingRemainsAndRatePlanFeatureEnabled, translationTab],
    );

    const onTariffChangeButtonClick = () => {
        openRoute(
            `${RoutePath.productsFromSubscriberId.replace(CLIENT_ID, subscriberId)}#${EProductsCategoryUrl.TARIFFS}`,
        );
    };

    const onMegaPowersButtonClick = () => {
        dispatch(productsSetPageFromAction({ pageFrom: EProductsPageFrom.SUBSCRIBER_ID_CHANGE_MEGA_POWERS }));
        dispatch(
            wizardStorageClearStepsAction({
                wizardName: EWizardName.PRODUCT_OFFERINGS,
            }),
        );
        openRoute(`${RoutePath.wizardProductOfferingsFromSubscriberIdOnlyMegaPowers.replace(CLIENT_ID, subscriberId)}`);
    };

    if (errorSubscriberInfoTariff) return <SubscriberIdError refetch={refetch} />;
    if (isSubscriberInfoTariffFetching) return <Skeleton isLoading render={SubscriberIdInfoTariffSkeleton} />;
    if (!subscriberInfoTariff) return null;

    return (
        <Fragment>
            <div className="subscriber-id-info__block subscriber-id-info__tariff">
                <div className="subscriber-id__title subscriber-id__title_light">{translationTab.tariff.title}</div>
                <div className="tariff__name">{subscriberInfoTariff.ratePlan.name}</div>
                <div
                    className={classNames('tariff__price', {
                        tariff__price_error: subscriberInfoTariff.ratePlan.payment?.error,
                    })}
                >
                    {formatProductPrice(subscriberInfoTariff.ratePlan)}
                </div>
                <div className="tariff__description">
                    <EllipsisText
                        lines={5}
                        maxHeight="300px"
                        overflow="auto"
                        title={subscriberInfoTariff.ratePlan.description}
                    >
                        {subscriberInfoTariff.ratePlan.description}
                    </EllipsisText>
                </div>
                {subscriberInfoTariff.ratePlan.nextPlan && (
                    <ArrowTooltip
                        maxWidth="300px"
                        placement="top"
                        title={stringFormatParams(translationTab.tariff.datePlanTooltip, {
                            [ELocaleKeys.date]: formatDate(subscriberInfoTariff.ratePlan.nextPlan.date),
                            [ELocaleKeys.name]: subscriberInfoTariff.ratePlan.nextPlan.name,
                        })}
                    >
                        <div>
                            <BadgeStatus
                                className="tariff__date-plan"
                                status={EBadgeStatus.changed}
                                text={stringFormatParams(translationTab.tariff.datePlan, {
                                    [ELocaleKeys.date]: formatDate(subscriberInfoTariff.ratePlan.nextPlan.date),
                                })}
                            />
                        </div>
                    </ArrowTooltip>
                )}
                {!!subscriberInfoTariff.options?.length && (
                    <div className="tariff__options">
                        <div className="tariff__options-title">{translationTab.tariffDiscounts.tariffOptions}</div>
                        {subscriberInfoTariff.options
                            .slice(0, isFullOptionsMode ? subscriberInfoTariff.options.length : optionsDrop)
                            .map((option, index) => (
                                <div className="tariff__option" key={index}>
                                    <div className="tariff__option-name">{option.name}</div>
                                    <div className="tariff__option-date">с {formatDate(option.date)}</div>
                                </div>
                            ))}
                        {subscriberInfoTariff.options.length > optionsDrop && (
                            <LinkBlock
                                dataName="subscriber-tariff-show-options-link"
                                onClick={() => setFullOptionsMode(!isFullOptionsMode)}
                            >
                                {isFullOptionsMode
                                    ? translationTab.tariffDiscounts.showShort
                                    : translationTab.tariffDiscounts.showAll}
                            </LinkBlock>
                        )}
                    </div>
                )}
                {subscriberInfoTariff.actions?.includes(ESubscriberIdActions.tariffChange) && (
                    <Button
                        className="tariff__btn"
                        dataName="subscriber-tariff-change"
                        onClick={onTariffChangeButtonClick}
                    >
                        {translationTab.tariff.btn}
                    </Button>
                )}
                {isMegaPowersFeature && subscriberInfoTariff.ratePlan.isMegaPowersAvailable && (
                    <SubscriberIdInfoMegaPowersStyled>
                        {(!!subscriberInfoTariff.megaPowers?.length ||
                            subscriberInfoTariff.ratePlan.isMegaPowersButtonVisible) && (
                            <Typography variant="h5">{translationTab.megaPowers.title}</Typography>
                        )}
                        {!!subscriberInfoTariff.megaPowers?.length && (
                            <MegaPowersList>
                                {subscriberInfoTariff.megaPowers?.map((megaPower, index) => (
                                    <TooltipText
                                        key={index}
                                        tooltipBody={
                                            megaPower.description && (
                                                <DangerouslySettedTextStyled>
                                                    <Typography isDangerousInnerHTML>
                                                        {megaPower.description}
                                                    </Typography>
                                                </DangerouslySettedTextStyled>
                                            )
                                        }
                                        tooltipProps={{ disablePortal: false }}
                                    >
                                        {addCommaToWordByIndex(
                                            megaPower.name,
                                            index,
                                            subscriberInfoTariff.megaPowers?.length,
                                        )}
                                    </TooltipText>
                                ))}
                            </MegaPowersList>
                        )}
                        {subscriberInfoTariff.ratePlan.isMegaPowersButtonVisible && (
                            <Button
                                className="megapowers_btn"
                                dataName="subscriber-megapowers-change"
                                kind="smallOutline"
                                onClick={onMegaPowersButtonClick}
                            >
                                {translationTab.megaPowers.btn}
                            </Button>
                        )}
                    </SubscriberIdInfoMegaPowersStyled>
                )}
            </div>
            <SubscriberIdInfoTariffDiscounts />
        </Fragment>
    );
};

export default withAsyncStateManagement(SubscriberIdInfoTariff, {
    sagas: productsSagas,
});
